import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./ui/alert-dialog";
import { Database, Download, RotateCcw, Trash2, Loader2, Plus, ShieldAlert } from "lucide-react";

function fmtSize(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export const BackupManager = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = () => api.get("/admin/backups").then((r) => setBackups(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const createBackup = async () => {
    setCreating(true);
    try {
      await api.post("/admin/backups");
      toast.success("Backup avviato. Comparirà nell'elenco tra qualche istante.");
      const startCount = backups.length;
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const { data } = await api.get("/admin/backups");
          setBackups(data);
          if (data.length > startCount) {
            toast.success(`Backup completato · ${data[0]?.total_documents ?? ""} documenti`.trim());
            setCreating(false);
            return;
          }
        } catch (_) { /* riprova */ }
        if (tries >= 24) { setCreating(false); return; }
        setTimeout(poll, 5000);
      };
      setTimeout(poll, 5000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setCreating(false);
    }
  };

  const download = async (b) => {
    setBusyId(b.id);
    try {
      const res = await api.get(`/admin/backups/${b.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = b.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const restore = async (b) => {
    setBusyId(b.id);
    try {
      const { data } = await api.post(`/admin/backups/${b.id}/restore`);
      const total = Object.values(data.restored || {}).reduce((a, v) => a + v, 0);
      toast.success(`Ripristino completato · ${total} documenti. Ricarico l'app...`);
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusyId(null);
    }
  };

  const remove = async (b) => {
    setBusyId(b.id);
    try {
      await api.delete(`/admin/backups/${b.id}`);
      toast.success("Backup eliminato");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card className="rounded border-border mt-8" data-testid="backup-manager">
      <div className="flex items-center justify-between p-5 border-b border-border">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 shrink-0 rounded bg-primary/10 flex items-center justify-center">
            <Database className="h-4 w-4 text-primary" strokeWidth={1.6} />
          </div>
          <div>
            <h2 className="font-heading font-semibold">Backup del database</h2>
            <p className="text-xs text-muted-foreground mt-0.5 max-w-xl">
              Copia completa di lead, clienti, preventivi, utenti, appuntamenti, prodotti, configurazioni e documenti.
              La creazione non interrompe il funzionamento dell'app. Il ripristino genera automaticamente un backup di sicurezza dello stato attuale.
              Un <b>backup automatico</b> viene eseguito <b>ogni notte alle 03:00</b> (ora italiana) con notifica email all'amministratore.
            </p>
          </div>
        </div>
        <Button onClick={createBackup} disabled={creating} className="font-semibold shrink-0" data-testid="create-backup-btn">
          {creating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />} Crea backup
        </Button>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-border">
              <TableHead className="text-xs uppercase tracking-wider">Data e ora</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Contenuto</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Dimensione</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Creato da</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-wider">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Caricamento...</TableCell></TableRow>
            ) : backups.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6" data-testid="no-backups">Nessun backup disponibile. Creane uno per iniziare.</TableCell></TableRow>
            ) : backups.map((b) => (
              <TableRow key={b.id} className="border-border" data-testid={`backup-row-${b.id}`}>
                <TableCell className="font-medium text-sm">
                  {new Date(b.created_at).toLocaleString("it-IT")}
                  {b.auto && <Badge variant="outline" className="ml-2 border-warning/40 text-warning font-normal text-[10px]">auto</Badge>}
                  {b.scheduled && <Badge variant="outline" className="ml-2 border-primary/40 text-primary font-normal text-[10px]">notturno</Badge>}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{b.total_documents} documenti</TableCell>
                <TableCell className="text-sm text-muted-foreground">{fmtSize(b.size_bytes)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{b.created_by_name || "—"}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => download(b)} disabled={busyId === b.id} data-testid={`download-backup-${b.id}`} title="Scarica">
                    <Download className="h-4 w-4" />
                  </Button>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" disabled={busyId === b.id} data-testid={`restore-backup-${b.id}`} title="Ripristina" className="text-primary hover:text-primary">
                        {busyId === b.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-popover" data-testid="restore-confirm-dialog">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="font-heading flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-warning" /> Ripristinare questo backup?</AlertDialogTitle>
                        <AlertDialogDescription>
                          I dati attuali verranno sostituiti con quelli del backup del <b>{new Date(b.created_at).toLocaleString("it-IT")}</b> ({b.total_documents} documenti).
                          Prima del ripristino viene salvato automaticamente un backup di sicurezza dello stato attuale, così l'operazione è reversibile.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="restore-cancel-btn">Annulla</AlertDialogCancel>
                        <AlertDialogAction onClick={() => restore(b)} data-testid="restore-confirm-btn" className="bg-primary">Sì, ripristina</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" disabled={busyId === b.id} data-testid={`delete-backup-${b.id}`} title="Elimina file di backup" className="text-destructive hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-popover">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="font-heading">Eliminare il file di backup?</AlertDialogTitle>
                        <AlertDialogDescription>Verrà rimosso solo questo file di backup. I dati dell'applicazione non vengono toccati.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(b)} className="bg-destructive text-destructive-foreground" data-testid="delete-backup-confirm-btn">Elimina</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
};
